import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Braces, Loader2, Upload, Trash2, ExternalLink, Archive } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const prevMonthKey = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export const BackupsPanel = ({ monthKey, defaultOpen = false }) => {
  const [rows, setRows] = useState([]);
  const [folder, setFolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/dailies/backups`, auth()); setRows(r.data.backups || []); setFolder(r.data.drive_folder || ''); return r.data.backups || []; } catch { return []; }
  }, []);
  useEffect(() => { load(); }, [load]);
  const pending = rows.some(b => b.status === 'running' || b.status === 'uploading');
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(async () => {
      const list = await load();
      const stillBusy = list.some(b => b.status === 'running' || b.status === 'uploading');
      if (!stillBusy) {
        const last = list.find(b => b.trigger === 'manual' && b.status === 'done');
        if (last) toast.success(`${monthLabel(last.month)} backed up — ${last.days} days, ${last.bars} bars${last.drive?.xlsx?.id ? ' · saved to Drive' : last.drive?.xlsx?.error ? ' · Drive upload failed (kept in Ops)' : ''}`);
        const failed = list.find(b => b.status === 'failed');
        if (failed) toast.error(`${monthLabel(failed.month)} backup failed: ${failed.error || 'unknown error'}`);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [pending, load]);

  const run = async (month) => {
    setBusy(true);
    try {
      await axios.post(`${API}/dailies/backups/run?month=${month}`, {}, auth());
      toast.info(`Building the ${monthLabel(month)} backup — the Drive upload takes a minute`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Backup failed'); }
    setBusy(false);
  };
  const download = async (b, kind) => {
    try {
      const r = await axios.get(`${API}/dailies/backups/${b.id}/download?kind=${kind}`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = kind === 'xlsx' ? b.xlsx_name : b.ops_name; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  const restore = async (file) => {
    if (!file) return;
    if (!window.confirm(`Restore “${file.name}”?\n\nThe dailies and schedule bars saved in it are put back exactly as they were (current entries for those dates are replaced).`)) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/dailies/backups/restore`, fd, { ...auth(), timeout: 180000 });
      toast.success(`Restored ${r.data.restored_days} days and ${r.data.restored_bars} bars from ${monthLabel(r.data.month)}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete the ${monthLabel(b.month)} backup from Ops? (Drive copies stay.)`)) return;
    try { await axios.delete(`${API}/dailies/backups/${b.id}`, auth()); load(); } catch { toast.error('Could not delete'); }
  };

  const prev = prevMonthKey();
  return (
    <div className="bg-white border border-slate-200 rounded-xl" data-testid="dailies-backups">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="dailies-backups-toggle">
        <span className="text-sm font-bold text-slate-900 inline-flex items-center gap-2"><Archive className="w-4 h-4 text-slate-500" />Month-end backups <span className="text-xs font-normal text-slate-500">· Excel like the dispatch sheet + .ops restore file · auto on the 1st, overnight</span></span>
        <span className="text-xs text-slate-500">{rows.length ? `${rows.length} saved` : 'none yet'} {open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => run(prev)} disabled={busy || pending} className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="dailies-backup-prev">
              {busy || pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}Back up {monthLabel(prev)} now
            </button>
            {monthKey && monthKey !== prev && (
              <button onClick={() => run(monthKey)} disabled={busy || pending} className="h-8 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50 disabled:opacity-60" data-testid="dailies-backup-shown">Back up {monthLabel(monthKey)} (shown)</button>
            )}
            <label className="h-8 px-3 rounded-lg border border-dashed border-slate-400 text-slate-700 text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer hover:bg-slate-50">
              <Upload className="w-3.5 h-3.5" />Restore a .ops file
              <input ref={fileRef} type="file" accept=".ops,application/json" className="hidden" onChange={e => restore(e.target.files?.[0])} data-testid="dailies-restore-input" />
            </label>
            {folder && <a href={`https://drive.google.com/drive/folders/${folder}`} target="_blank" rel="noreferrer" className="ml-auto text-xs text-sky-700 hover:underline inline-flex items-center gap-1" data-testid="dailies-drive-folder"><ExternalLink className="w-3.5 h-3.5" />Drive folder</a>}
          </div>
          {rows.length === 0 ? <p className="text-xs text-slate-500">No backups yet. The first one is written automatically on the 1st of next month.</p> : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
              {rows.map(b => (
                <div key={b.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`dailies-backup-${b.id}`}>
                  <div className="min-w-[160px]">
                    <div className="font-semibold text-slate-900">{monthLabel(b.month)}</div>
                    <div className="text-[11px] text-slate-500">{new Date(b.created_at).toLocaleString()} · {b.trigger === 'auto' ? 'month rollover' : b.created_by} · {b.days} days · {b.bars} bars</div>
                  </div>
                  {(b.status === 'running' || b.status === 'uploading') ? (
                    <span className="text-xs text-amber-700 inline-flex items-center gap-1.5" data-testid={`dailies-backup-status-${b.id}`}><Loader2 className="w-3.5 h-3.5 animate-spin" />{b.status === 'running' ? 'Building files…' : 'Uploading to Drive…'}</span>
                  ) : b.status === 'failed' ? (
                    <span className="text-xs text-red-700" title={b.error} data-testid={`dailies-backup-status-${b.id}`}>Failed — {b.error}</span>
                  ) : (<>
                  <button onClick={() => download(b, 'xlsx')} className="h-7 px-2.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-xs font-semibold inline-flex items-center gap-1" data-testid={`dailies-backup-xlsx-${b.id}`}><FileSpreadsheet className="w-3.5 h-3.5" />Excel <span className="text-emerald-600 font-normal">{fmtSize(b.xlsx_size)}</span></button>
                  <button onClick={() => download(b, 'ops')} className="h-7 px-2.5 rounded-md border border-slate-300 bg-slate-50 text-slate-800 text-xs font-semibold inline-flex items-center gap-1" data-testid={`dailies-backup-ops-${b.id}`}><Braces className="w-3.5 h-3.5" />.ops <span className="text-slate-500 font-normal">{fmtSize(b.ops_size)}</span></button>
                  {b.drive?.xlsx?.link && <a href={b.drive.xlsx.link} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" />in Drive</a>}
                  {b.drive?.xlsx?.error && <span className="text-[11px] text-red-700" title={b.drive.xlsx.error}>Drive upload failed</span>}
                  </>)}
                  <button onClick={() => remove(b)} className="ml-auto p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" title="Delete from Ops" data-testid={`dailies-backup-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
